/**
 * Produtos — Provider + hook compartilhados (fonte única de verdade).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { productService, ProductDTO } from '../../../services/modules/productService';

interface ProductsContextData {
    products: ProductDTO[];
    loading: boolean;
    error: string | null;
    refreshProducts: () => Promise<void>;
    createProduct: (productData: { name: string; value: number; description: string }) => Promise<ProductDTO>;
    updateProduct: (id: string, updates: { name?: string; value?: number; description?: string }) => Promise<ProductDTO>;
    deleteProduct: (id: string) => Promise<void>;
    duplicateProduct: (product: ProductDTO) => Promise<ProductDTO>;
}

const ProductsContext = createContext<ProductsContextData | undefined>(undefined);

export const ProductsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const [products, setProducts] = useState<ProductDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentOrganization) {
                setProducts([]);
                setLoading(false);
                return;
            }

            const data = await productService.getAll(currentOrganization.id);
            setProducts(data);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erro ao carregar produtos';
            console.error('[useProducts] Error fetching products:', err);
            setError(message);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const createProduct = useCallback(async (productData: {
        name: string;
        value: number;
        description: string;
    }) => {
        if (!currentOrganization) throw new Error('No organization selected');

        const newProduct = await productService.create(
            productData.name,
            productData.value,
            productData.description,
            currentOrganization.id,
            user?.id
        );
        setProducts(prev => [newProduct, ...prev]);
        return newProduct;
    }, [currentOrganization?.id, user?.id]);

    const updateProduct = useCallback(async (
        id: string,
        updates: { name?: string; value?: number; description?: string }
    ) => {
        if (!currentOrganization) throw new Error('No organization selected');

        const updatedProduct = await productService.update(id, updates, currentOrganization.id);
        setProducts(prev => prev.map(p => (p.id === id ? updatedProduct : p)));
        return updatedProduct;
    }, [currentOrganization?.id]);

    const deleteProduct = useCallback(async (id: string) => {
        await productService.delete(id);
        setProducts(prev => prev.filter(p => p.id !== id));
    }, []);

    const duplicateProduct = useCallback(async (product: ProductDTO) => {
        if (!currentOrganization) throw new Error('No organization selected');

        const duplicatedProduct = await productService.create(
            `${product.name} (Cópia)`,
            product.value,
            product.description,
            currentOrganization.id,
            user?.id
        );
        setProducts(prev => [duplicatedProduct, ...prev]);
        return duplicatedProduct;
    }, [currentOrganization?.id, user?.id]);

    useEffect(() => {
        if (currentOrganization) {
            fetchProducts();
        }
    }, [currentOrganization?.id, fetchProducts]);

    const value: ProductsContextData = {
        products,
        loading,
        error,
        refreshProducts: fetchProducts,
        createProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
    };

    return React.createElement(ProductsContext.Provider, { value }, children);
};

export const useProducts = (): ProductsContextData => {
    const context = useContext(ProductsContext);
    if (!context) {
        throw new Error('useProducts must be used within ProductsProvider');
    }
    return context;
};
